import { usePathname } from 'router'
import links from 'links'
import { useExpiration } from 'hooks'
import useFt from 'hooks/useFt'
import { constants } from 'helpers'
import { useUser } from 'modules/user'
import { useCandleSubscription, useDriftSubscription, useSubscription } from 'modules/subscription'
import { usePrivateSale } from 'modules/products'


const normalizePagePaths = (pagePaths: string[]) => {
  return pagePaths
    .map((link) => link.replace('(.*)', '').replace(':slug', ''))
    .join('|')
}

const productPagePaths = normalizePagePaths([
  links.productGeneral,
  links.productCandle,
  links.productExtra,
  links.productMakeup,
  links.productPerfume,
  links.productPerfumeLanding,
  links.productPersonalCare,
  links.productSkincare,
  links.productWellness,
  links.productDiffuser,
  links.productAccessory,
])

const productPagePathRegExp = new RegExp(`^(${productPagePaths})`)

const useBanner = () => {
  const pathname = usePathname()
  const { isLoggedIn, isFetching: isUserFetching } = useUser()
  const { subscription, isFetching: isSubscriptionFetching } = useSubscription()
  const { privateSale, isFetching: isPrivateSaleFetching } = usePrivateSale()
  const { candleSubscription, isFetching: isCandleSubscriptionFetching } = useCandleSubscription()
  const { driftSubscription } = useDriftSubscription()

  const isGiftHubEnabled = useFt(constants.features.giftHub)
  const isHolidayMothersDayEnabled = useFt(constants.features.holidayMothersDay)
  const isHolidayFathersDayEnabled = useFt(constants.features.holidayFathersDay)

  const is15percentDiscountEnabled = useFt(constants.features.holiday22SaleBadge15)
  const is20percentDiscountEnabled = useFt(constants.features.holiday22SaleBadge20)
  const isBlackFridayBannerEnabled = useFt(constants.features.blackFridayTopHomePageBanner)
  const isCyberMondayBannerEnabled = useFt(constants.features.cyberMondayTopHomePageBanner)

  const isDriftSubscriptionEnabled = useFt(constants.features.driftSubscription)
  const isDriftSubscriptionLaunchPromoEnabled = useFt(constants.features.driftSubscriptionLaunchPromo)

  const isBfcmNotificationEnabled = useFt(constants.features.bfcmNotification)
  const isGiftSubscriptionNotificationEnabled = useFt(constants.features.giftSubscriptionNotification)

  const isFetching = isUserFetching || isSubscriptionFetching || isPrivateSaleFetching || isCandleSubscriptionFetching

  const isSubscribeBannerVisible = (
    subscription?.hasNeverSubscribed
    && pathname !== links.subscription.main
    && !productPagePathRegExp.test(pathname)
  )

  // show banner only when subscription is in undeliverable status
  const isUndeliverableBannerVisible = subscription?.isUndeliverable

  const isCardUpdateBannerVisible = subscription?.isUnpaid

  const isTossInBannerVisible = (
    isLoggedIn
    && pathname !== links.tossIn
    && subscription?.canApplyTossInOffer
  )

  const isHolidayBanner15Visible = (
    is15percentDiscountEnabled && pathname !== links.shop.holiday
  )

  const isHolidayBanner20Visible = (
    is20percentDiscountEnabled && pathname !== links.shop.holiday
  )

  const isBfcmBannerVisible = (
    isBfcmNotificationEnabled
    && pathname === links.shop.holiday
  )

  const isGiftSubscriptionBannerVisible = (
    isGiftSubscriptionNotificationEnabled
    && pathname === links.giftSubscription
  )

  const isHolidayBannerVisible = (
    isGiftHubEnabled
    && !isHolidayMothersDayEnabled
    && !isHolidayFathersDayEnabled
    && !isBfcmBannerVisible
    && !isGiftSubscriptionBannerVisible
    && pathname !== links.gifts
  )

  const isHolidayMothersDayVisible = (
    isGiftHubEnabled
    && isHolidayMothersDayEnabled
    && pathname !== links.gifts
  )

  const isHolidayFathersDayVisible = (
    isGiftHubEnabled
    && isHolidayFathersDayEnabled
    && pathname !== links.gifts
  )

  const isBlackFridayBannerVisible = (
    subscription?.hasNeverSubscribed
    && isBlackFridayBannerEnabled
    && (
      pathname === links.home || pathname === links.homeMen
    )
  )

  const isCyberMondayBannerVisible = (
    subscription?.hasNeverSubscribed
    && isCyberMondayBannerEnabled
    && (
      pathname === links.home || pathname === links.homeMen
    )
  )

  const isPrivateSaleExpired = useExpiration({ timeLeft: privateSale?.timeLeft })
  const isPrivateSaleBannerVisible = (
    isLoggedIn
    && pathname !== links.privateSale
    && Boolean(privateSale)
    && !isPrivateSaleExpired
  )

  const isUnskipBannerVisible = (
    isLoggedIn
    && subscription?.isOnHold
    && Boolean(subscription?.activeHold)
  )

  const isCandleSubscriptionBannerVisible = (
    candleSubscription?.isEnabled
    && !candleSubscription?.isSelected
    && pathname !== links.candleSubscription
    && pathname !== links.featuredCandle
  )

  const isDriftSubscriptionVisible = (
    isDriftSubscriptionEnabled
    && driftSubscription?.isEnabled
    && !driftSubscription?.isSelected
    && pathname !== links.driftSubscription
  )
  const isDriftSubscriptionHasLaunchPriority = isDriftSubscriptionLaunchPromoEnabled

  const isResubscribeBannerVisible = Boolean(subscription?.isCancelled)


  return {
    isFetching,
    isCardUpdateBannerVisible,
    isSubscribeBannerVisible,
    isUndeliverableBannerVisible,
    isTossInBannerVisible,
    isPrivateSaleBannerVisible,
    isUnskipBannerVisible,
    isCandleSubscriptionBannerVisible,
    isHolidayBannerVisible,
    isHolidayBanner15Visible,
    isHolidayBanner20Visible,
    isHolidayMothersDayVisible,
    isHolidayFathersDayVisible,
    isBlackFridayBannerVisible,
    isCyberMondayBannerVisible,
    isResubscribeBannerVisible,
    isDriftSubscriptionVisible,
    isDriftSubscriptionHasLaunchPriority,
    isBfcmBannerVisible,
    isGiftSubscriptionBannerVisible,
  }
}


export default useBanner
